import type {
  ActivityBlock,
  ActivityFormValues,
  ActivitySubmitAction,
} from "./ActivityCreatePage.types";

export const responseTimeOptions = [
  { key: "SHORT", label: "SHORT (e.g. single word answers) (10s)", seconds: 10 },
  { key: "MEDIUM", label: "MEDIUM (e.g. categorization) (30s)", seconds: 30 },
  { key: "LONG", label: "LONG (45s)", seconds: 45 },
] as const;

export const emptyBlock = (contentType: ActivityBlock["contentType"]): ActivityBlock => ({
  contentType,
  text: "",
  questionType: "",
  silenceBeforeResponseSec: null,
  responsePreset: "MEDIUM",
  isScorable: false,
  scoringNotes: "",
  expectedAnswers: contentType === "QUESTION" ? [] : null,
});

/** Full reset shape for useFieldArray (`contents`) and controlled register fields after save / reset UI. */
export const emptyActivityFormValues = (): ActivityFormValues => ({
  title: "",
  description: "",
  category: null,
  difficulty: null,
  activityTypeKeys: [],
  notes: "",
  contents: [],
});

export const buildDraftActivityJSON = (data: ActivityFormValues) => {
  const draftActivityJSON = { ...data };
  // Only send activity type keys to backend for cognitive exercises, as it's not used for other
  // categories. Useful to keep the activity types while editing, in case the user changes the
  // category while editing, and doesn't want to lose the activity type they've selected. When
  // other categories are selected, the activity types section is hidden.
  if (data.category !== "COGNITIVE_EXERCISE") {
    delete draftActivityJSON.activityTypeKeys;
  }

  return {
    ...draftActivityJSON,
    contents: draftActivityJSON.contents?.map((content) => {
      if (content.contentType !== "QUESTION") {
        return content;
      }

      const { responsePreset, silenceBeforeResponseSec, ...rest } = content;
      const silenceNum = silenceBeforeResponseSec && Number(silenceBeforeResponseSec);
      const silenceForPayload = silenceNum && Number.isFinite(silenceNum) ? silenceNum : undefined;

      return {
        ...rest,
        responseTimeSeconds:
          responseTimeOptions.find((option) => option.key === responsePreset)?.seconds ?? 0,
        ...(silenceForPayload ? { silenceBeforeResponseSec: silenceForPayload } : {}),
      };
    }),
  };
};

const getResponsePresetFromSeconds = (seconds: number | undefined) => {
  if (!seconds) {
    return "MEDIUM";
  }

  const foundOption = responseTimeOptions.find((option) => option.seconds === seconds);
  if (foundOption) {
    return foundOption.key;
  }

  if (seconds <= responseTimeOptions[0].seconds) {
    return responseTimeOptions[0].key;
  }

  if (seconds >= responseTimeOptions[2].seconds) {
    return responseTimeOptions[2].key;
  }

  return responseTimeOptions[1].key;
};

export const buildFormValuesFromDraftActivityJSON = (activityJSON: unknown): ActivityFormValues => {
  if (!activityJSON || typeof activityJSON !== "object") {
    return emptyActivityFormValues();
  }

  const parsedActivityJSON = activityJSON as ActivityFormValues;
  const parsedActivityContents = (parsedActivityJSON.contents ?? []) as Array<
    ActivityBlock & { responseTimeSeconds?: number }
  >;

  return {
    ...parsedActivityJSON,
    contents: parsedActivityContents.map((content) => {
      if (content.contentType !== "QUESTION") {
        return content;
      }

      const { responseTimeSeconds, ...rest } = content;
      return {
        ...rest,
        responsePreset: getResponsePresetFromSeconds(responseTimeSeconds),
      };
    }),
  };
};

export const validateTitleBySubmitAction = (
  value: string | undefined,
  submitAction: ActivitySubmitAction
) => {
  if (value?.trim()) {
    return true;
  }
  return submitAction === "draft"
    ? "Title is required to save draft."
    : "Title is required to publish.";
};
